import React, { useRef } from "react";
import { Autoplay, Pagination } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link } from "react-router-dom";

import mainImg from "assets/img/Main Images/Home/bg.webp";
import services3dImg from "assets/img/Main Images/Home/ghar360_banner.webp";
import services2dImg from "assets/img/Main Images/Home/Arrise Banner.webp";
import ghar360Img from "assets/img/Main Images/Home/concept_to_3d.webp";
import data from "data/home-data.json";

const STUDIO_SLIDE_DELAY = 8000;
const SLIDE_DELAY = 5000;

const slides = [
  {
    id: 1,
    title: data?.home.banner.heading,
    description: data?.home.banner.description,
    image: mainImg,
    imageAlt: "Rionick creative character",
    imageClass: "home-hero-slide__image--character",
    actions: [
      { label: "Get a Quote", to: "/contact-us" },
      { label: "View Our Work", href: "#home-portfolio" },
    ],
  },
  {
    id: 4,
    title: "From Concept To Digital Reality",
    description:
      "Whether it's CGI, 3D visualization, animation, branding, websites, or digital marketing, our team delivers creative solutions tailored to help businesses grow with confidence.",
    image: ghar360Img,
    imageAlt: "Ghar360 interactive property walkthrough",
    actions: [
      { label: "Get a Quote", to: "/contact-us" },
      { label: "View Our Work", href: "#home-portfolio" },
    ],
  },
  {
    id: 2,
    title: "Experience Properties Before They Exist",
    description:
      "Ghar360 enables buyers to explore apartments through real-time interactive walkthroughs with furnished views, day & night modes, AI-guided tours, and immersive property experiences.",
    image: services3dImg,
    imageAlt: "Rionick 3D production pipeline",
    actions: [
      { label: "Explore Ghar360", to: "/ghar360" },
      { label: "Get a Quote", to: "/contact-us" },
    ],
  },
  {
    id: 3,
    title: "Transform Every Print Into an Experience",
    description:
      "ARRISE brings business cards, brochures, packaging, posters, and billboards to life with interactive augmented reality experiences that engage customers beyond traditional marketing.",
    image: services2dImg,
    imageAlt: "Rionick 2D creative artwork",
    actions: [
      { label: "Explore ARRISE", href: "https://ar.rionick.com/" },
      { label: "Get a Quote", to: "/contact-us" },
    ],
  },
];

const HomeStartupBanner = () => {
  const heroRef = useRef(null);

  const handleAmbientMove = (event) => {
    if (!heroRef.current || window.innerWidth < 992) return;

    const targets = heroRef.current.querySelectorAll("[data-repel]");
    targets.forEach((target) => {
      const rect = target.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const deltaX = centerX - event.clientX;
      const deltaY = centerY - event.clientY;
      const distance = Math.hypot(deltaX, deltaY);
      const radius = Number(target.dataset.repelRadius || 185);

      if (distance < radius) {
        const force = (radius - distance) / radius;
        const angle = Math.atan2(deltaY, deltaX);
        const strength = Number(target.dataset.repelStrength || 46);
        target.style.setProperty("--repel-x", `${Math.cos(angle) * force * strength}px`);
        target.style.setProperty("--repel-y", `${Math.sin(angle) * force * strength}px`);
        target.style.setProperty("--repel-glow", force.toFixed(3));
      } else {
        target.style.setProperty("--repel-x", "0px");
        target.style.setProperty("--repel-y", "0px");
        target.style.setProperty("--repel-glow", "0");
      }
    });
  };

  const resetAmbientMove = () => {
    if (!heroRef.current) return;

    heroRef.current.querySelectorAll("[data-repel]").forEach((target) => {
      target.style.setProperty("--repel-x", "0px");
      target.style.setProperty("--repel-y", "0px");
      target.style.setProperty("--repel-glow", "0");
    });
  };

  return (
    <section
      ref={heroRef}
      className="slider__area home-hero pt-180 z-index-1 p-relative fix"
      onMouseMove={handleAmbientMove}
      onMouseLeave={resetAmbientMove}
    >
      <div className="home-hero__ambient" aria-hidden="true">
        <span className="home-hero__glow home-hero__glow--red"></span>
        <span className="home-hero__glow home-hero__glow--blue"></span>
        <span className="home-hero__orbit home-hero__orbit--one" data-repel data-repel-strength="34"></span>
        <span className="home-hero__orbit home-hero__orbit--two" data-repel data-repel-strength="30"></span>
        <span className="home-hero__chip home-hero__chip--one" data-repel>3D</span>
        <span className="home-hero__chip home-hero__chip--two" data-repel>AR</span>
        <span className="home-hero__chip home-hero__chip--three" data-repel data-repel-strength="54">Ghar360</span>
        <span className="home-hero__spark home-hero__spark--one" data-repel data-repel-radius="120" data-repel-strength="28"></span>
        <span className="home-hero__spark home-hero__spark--two" data-repel data-repel-radius="120" data-repel-strength="28"></span>
      </div>
      <Swiper
        className="home-hero-slider"
        slidesPerView={1}
        spaceBetween={0}
        speed={700}
        loop={true}
        grabCursor={true}
        autoplay={{
          delay: STUDIO_SLIDE_DELAY,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        // The studio message (first slide) stays up longer than the product slides
        onRealIndexChange={(swiper) => {
          swiper.params.autoplay.delay =
            swiper.realIndex === 0 ? STUDIO_SLIDE_DELAY : SLIDE_DELAY;
        }}
        pagination={{
          el: ".home-hero-pagination",
          clickable: true,
        }}
        modules={[Autoplay, Pagination]}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            <div className="container">
              <div className="row align-items-center home-hero-slide">
                <div className="col-xxl-6 col-xl-6 col-lg-6">
                  <div className="slider__content-7 home-hero-slide__content">
                    {slide.id === 1 ? (
                      <h1 className="slider__title-5">{slide.title}</h1>
                    ) : (
                      <h3 className="slider__title-5">{slide.title}</h3>
                    )}
                    <p>{slide.description}</p>
                    <div className="home-hero-slide__actions">
                      {slide.actions.map((action, index) => {
                        const className =
                          index === 0
                            ? "tp-btnr bg-danger tp-btn-shine-effect"
                            : "tp-btnr-border tp-btnr-border-red tp-btn-shine-effect tp-link-btn-3";
                        return action.to ? (
                          <Link key={action.label} to={action.to} className={className}>
                            {action.label}
                          </Link>
                        ) : (
                          <a
                            key={action.label}
                            href={action.href}
                            className={className}
                            {...(action.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
                          >
                            {action.label}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="col-xxl-6 col-xl-6 col-lg-6">
                  <div className="slider__thumb-wrapper-7 home-hero-slide__media">
                    <div className="slider__thumb-7">
                      <img
                        className={slide.imageClass || ""}
                        src={slide.image}
                        alt={slide.imageAlt}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="home-hero-pagination tp-swiper-dot text-center p-relative z-index-1"></div>
    </section>
  );
};

export default HomeStartupBanner;
